<template>
  <el-container style="margin: 0; min-height: 100vh;">
    <el-main style="max-width: 1200px; margin: 0 auto; padding-top: 30px;">

      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
        <h1 class="text-center title" style="margin: 0; flex-grow: 1; text-align: center;">知音</h1>
        <div style="width: 100px; text-align: right;">
             <el-button v-if="!isLoggedIn" type="primary" size="small" @click="loginDialogVisible = true">登录</el-button>
             <div v-else style="display: flex; align-items: center; justify-content: flex-end; gap: 10px;">
                 <span style="font-size: 12px; color: #666;">{{ username }}</span>
                 <el-button type="info" size="small" @click="handleLogout">退出</el-button>
             </div>
        </div>
      </div>

      <el-tabs v-model="activeTab" type="border-card" class="custom-tabs" :before-leave="handleTabBeforeLeave">
        <!-- Tab 1: Playlist Parser -->
        <el-tab-pane label="歌单解析" name="parser">
          <PlaylistParser />
        </el-tab-pane>

        <!-- Tab 2: Recommendation -->
        <el-tab-pane label="歌单推荐" name="recommendation" lazy>
           <AiRecommendation
             :user-config="commonState"
             :ai-models="aiModels"
           />
        </el-tab-pane>

        <!-- Tab 3: System Settings -->
        <el-tab-pane label="系统设置" name="settings" lazy>
          <SystemSettings
            :ai-models="aiModels"
            @models-changed="loadAIModels"
          />
        </el-tab-pane>

        <!-- Tab 4: Recommendation Eval Harness (只读查看，Phase 1b) -->
        <el-tab-pane label="评测" name="eval" lazy>
          <EvalDashboard />
        </el-tab-pane>
      </el-tabs>

    </el-main>

    <div style="position: fixed; bottom: 10px; right: 20px; color: #aaa; font-size: 12px; z-index: 999;">
       Version: {{ version }}
    </div>

    <LoginDialog
      v-model:visible="loginDialogVisible"
      @login-success="onLoginSuccess"
    />

  </el-container>
</template>

<script setup>
import { reactive, ref, onMounted, watch } from 'vue';
import axios from 'axios';
import { ElMessage } from 'element-plus';
import PlaylistParser from './components/PlaylistParser.vue';
import AiRecommendation from './components/AiRecommendation.vue';
import SystemSettings from './components/SystemSettings.vue';
import LoginDialog from './components/LoginDialog.vue';
import EvalDashboard from './components/EvalDashboard.vue';

const version = process.env.VUE_APP_VERSION;

const activeTab = ref('parser'); // 歌单解析为默认及唯一公开 Tab

// Auth Logic
const isLoggedIn = ref(false);
const username = ref('');
const loginDialogVisible = ref(false);

const checkAuth = async () => {
  try {
    const resp = await axios.get('/api/auth/status');
    if (resp.data.data.is_authenticated) {
      isLoggedIn.value = true;
      username.value = resp.data.data.username;
    } else {
      isLoggedIn.value = false;
    }
  } catch (err) {
    console.error("Auth check failed", err);
  }
};

const onLoginSuccess = (userData) => {
    isLoggedIn.value = true;
    username.value = userData.username;
    // Reload protected data
    loadUserConfig();
    loadAIModels();
};

const handleLogout = async () => {
    try {
        await axios.post('/api/auth/logout');
        isLoggedIn.value = false;
        username.value = '';
        activeTab.value = 'parser'; // 退出后切回公开 Tab
        ElMessage.success('已退出登录');
    } catch (err) {
        console.error('Logout failed', err);
    }
};

const handleTabBeforeLeave = (activeName) => {
    // If not logged in and trying to go to protected tabs
    // （评测 Tab 消费 /api/eval/* 系列接口，同样受全局 session 登录保护，见后端 require_login）
    if (!isLoggedIn.value && ['recommendation', 'settings', 'eval'].includes(activeName)) {
        loginDialogVisible.value = true;
        return false; // Prevent tab switch
    }
    return true;
};

// Global Axios Interceptor for 401
let lastLoginWarningTime = 0;
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      // Ignore 401 from auth status check to avoid loop/initial popup
      if (error.config.url && error.config.url.includes('/api/auth/status')) {
          return Promise.reject(error);
      }

      if (!isLoggedIn.value) {
         loginDialogVisible.value = true;
         // Debounce warning message (2 seconds)
         const now = Date.now();
         if (now - lastLoginWarningTime > 2000) {
             ElMessage.warning('请先登录');
             lastLoginWarningTime = now;
         }
      }
    }
    return Promise.reject(error);
  }
);

onMounted(async () => {
    await checkAuth();
    // Only load protected data if logged in
    loadUserConfig(); // Has internal fallback
    if (isLoggedIn.value) {
        loadAIModels();
    }
});

// Common Input State
const commonState = reactive({
  prompt: '',
  user_profile: '',  // 用户画像设定
  user_playlist: '', // 用户歌曲记录
  enable_search: false
});

// Persistence - 从后端加载配置
const loadUserConfig = async () => {
  let loadedFromServer = false;
  if (isLoggedIn.value) {
      try {
        const resp = await axios.get('/api/user_config/');
        if (resp.data.code === 1) {
          const config = resp.data.data;
          commonState.user_profile = config.user_profile || '';
          commonState.prompt = config.user_prompt || '';
          commonState.user_playlist = config.user_playlist || '';
          loadedFromServer = true;
        }
      } catch (err) {
        console.error('加载用户配置失败:', err);
      }
  }

  if (!loadedFromServer) {
    // 降级到 localStorage（向后兼容）
    const savedProfile = localStorage.getItem('musicagent_user_profile');
    if (savedProfile !== null) commonState.user_profile = savedProfile;

    const savedUser = localStorage.getItem('musicagent_user_prompt');
    if (savedUser !== null) commonState.prompt = savedUser;

    const savedPlaylist = localStorage.getItem('musicagent_user_playlist');
    if (savedPlaylist !== null) commonState.user_playlist = savedPlaylist;
  }
};

// 防抖保存函数
let saveTimer = null;
const saveUserConfig = (field, value) => {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    // Only save to server if logged in
    if (isLoggedIn.value) {
        try {
          await axios.put('/api/user_config/', {
            [field]: value
          });
        } catch (err) {
          console.error('保存用户配置失败:', err);
        }
    }

    // Always save to localStorage as backup/offline
    const keyMap = {
        'user_profile': 'musicagent_user_profile',
        'user_prompt': 'musicagent_user_prompt',
        'user_playlist': 'musicagent_user_playlist'
    };
    localStorage.setItem(keyMap[field], value);
  }, 500); // 500ms 防抖
};

watch(() => commonState.user_profile, (val) => saveUserConfig('user_profile', val));
watch(() => commonState.prompt, (val) => saveUserConfig('user_prompt', val));
watch(() => commonState.user_playlist, (val) => saveUserConfig('user_playlist', val));

// AI Model Management (Shared Data)
const aiModels = ref([]);
const loadAIModels = async () => {
    try {
        const resp = await axios.get('/api/models/all');
        if (resp.data.code === 1) {
            aiModels.value = resp.data.data;
        }
    } catch (err) {
        console.error('Failed to load AI models', err);
    }
};

</script>

<style>
body {
  margin: 0;
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "微软雅黑", Arial, sans-serif;
  background-color: #f5f7fa;
}


.title {
  color: #409EFF;
  margin-bottom: 30px;
  font-weight: bold;
}

.text-center {
  text-align: center;
}
</style>
